import { StatCard, SectionTitle, FunnelBar, TableSkeleton } from "../components";
import { formatMins, formatDuration, deltaSub } from "../format";

/* Analytics — the lean daily pulse: a real-time "live now" row, a range-driven
   growth row, and the conversion funnel. Composes /lobby + /doctors (live) and
   /analytics + /insights (range). */
export const AnalyticsTab = ({ lobby, doctors, analytics, insights }) => {
  const p = analytics.patients;
  const v = analytics.visits;

  return (
    <>
      {/* Live now — always real-time, independent of the range */}
      <SectionTitle>Live Now</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Currently Waiting"
          value={lobby ? lobby.waitingCount : "…"}
          sub="live queue"
          alert={!!lobby && (lobby.uncoveredStates?.length || 0) > 0}
        />
        <StatCard
          label="Avg Wait"
          value={lobby ? formatMins(lobby.avgWaitMins) : "…"}
          sub="patients waiting now"
        />
        <StatCard
          label="Active Visits"
          value={doctors ? doctors.activeVisitsTotal : "…"}
          sub="in progress"
        />
        <StatCard
          label="Online Providers"
          value={doctors ? (doctors.presenceAvailable ? doctors.onlineCount : "—") : "…"}
          sub={
            doctors
              ? doctors.presenceAvailable
                ? "via SendBird presence"
                : "presence unavailable"
              : "loading"
          }
          alert={!!doctors && doctors.presenceAvailable && doctors.onlineCount === 0}
        />
      </div>

      {/* Growth — follows the range pills */}
      <SectionTitle>Growth</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="New Signups"
          value={p.new}
          sub={deltaSub(p.new, p.prevNew) || "patient accounts created"}
        />
        <StatCard
          label="Signup → Visit"
          value={p.activationRate != null ? `${p.activationRate}%` : "—"}
          sub={
            p.new
              ? `${p.activated} of ${p.new} signups started a visit${p.sampleCapped ? " (sampled)" : ""}`
              : "no signups in period"
          }
          alert={p.activationRate != null && p.activationRate < 50}
        />
        <StatCard
          label="Visits"
          value={v.created}
          sub={deltaSub(v.created, v.prevCreated) || "visits created"}
        />
        <StatCard
          label="Repeat Users"
          value={v.repeatVisitRate != null ? `${v.repeatVisitRate}%` : "—"}
          sub={`${v.repeatVisits} of ${v.created} visits were repeat`}
        />
      </div>

      {/* Conversion funnel — full section: efficiency metrics + funnel bars */}
      <SectionTitle>Conversion Funnel</SectionTitle>
      {!insights ? (
        <TableSkeleton rows={4} cols={4} />
      ) : (
        <>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            label="Fill Rate"
            value={insights.fillRate != null ? `${insights.fillRate}%` : "—"}
            sub="lobby entries → accepted"
            alert={insights.fillRate != null && insights.fillRate < 70}
          />
          <StatCard
            label="Completion Rate"
            value={insights.completionRate != null ? `${insights.completionRate}%` : "—"}
            sub="accepted → completed"
            alert={insights.completionRate != null && insights.completionRate < 90}
          />
          <StatCard
            label="Lobby Abandonment"
            value={insights.abandonment.rate != null ? `${insights.abandonment.rate}%` : "—"}
            sub={
              insights.abandonment.cancelled
                ? `${insights.abandonment.cancelled} cancelled · avg ${formatMins(
                    insights.abandonment.avgMinsBeforeCancel
                  )} before giving up`
                : "no cancellations in period"
            }
            alert={insights.abandonment.rate != null && insights.abandonment.rate > 10}
          />
          <StatCard
            label="Time To Accept"
            value={insights.timeToAccept.p50Secs != null ? `${formatDuration(insights.timeToAccept.p50Secs)} p50` : "—"}
            sub={
              insights.timeToAccept.samples
                ? `p90 ${formatDuration(insights.timeToAccept.p90Secs)} · max ${formatDuration(
                    insights.timeToAccept.maxSecs
                  )}`
                : "no accepted visits in period"
            }
            alert={insights.timeToAccept.p90Secs != null && insights.timeToAccept.p90Secs > 900}
          />
        </div>
        <div className="rounded-lg bg-white shadow-lg px-6 py-4 flex flex-col gap-3">
          <FunnelBar label="Created" count={insights.funnel.created} base={insights.funnel.created} />
          <FunnelBar
            label="Reached Lobby"
            count={insights.funnel.reachedLobby}
            base={insights.funnel.created}
            dropNote={
              insights.funnel.created - insights.funnel.reachedLobby
                ? `-${insights.funnel.created - insights.funnel.reachedLobby}`
                : ""
            }
          />
          <FunnelBar
            label="Accepted"
            count={insights.funnel.accepted}
            base={insights.funnel.created}
            dropNote={
              insights.funnel.reachedLobby - insights.funnel.accepted
                ? `-${insights.funnel.reachedLobby - insights.funnel.accepted}`
                : ""
            }
          />
          <FunnelBar
            label="Completed"
            count={insights.funnel.completed}
            base={insights.funnel.created}
            dropNote={
              insights.funnel.accepted - insights.funnel.completed > 0
                ? `-${insights.funnel.accepted - insights.funnel.completed}`
                : ""
            }
          />
          {insights.funnel.cancelled > 0 && (
            <span className="text-xs text-red-600">
              {insights.funnel.cancelled} visits cancelled in period
            </span>
          )}
        </div>
        </>
      )}
    </>
  );
};
